import { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import { isWeb } from '@helpers';
import type { Annotation, TextAnnotation, TextEditingRequest } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import { AnnotationType } from '@v2/components/imageHandling/components/DrawingCanvas/types';

export interface UseTextEditingParams {
  annotations: Annotation[];
  setAnnotations: (next: Annotation[]) => void;
  pushUndoSnapshot: () => void;
}

export const useTextEditing = ({ annotations, setAnnotations, pushUndoSnapshot }: UseTextEditingParams) => {
  const [editing, setEditing] = useState<TextEditingRequest | null>(null);
  const [textValue, setTextValue] = useState<string>('');
  const textValueRef = useRef<string>('');
  textValueRef.current = textValue;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const requestEditing = useCallback(
    (request: TextEditingRequest) => {
      const existing = annotations.find((a) => a.id === request.annotationId);
      setTextValue(existing && existing.type === AnnotationType.Text ? (existing as TextAnnotation).text : '');
      setEditing(request);
    },
    [annotations]
  );

  const commit = useCallback(() => {
    if (!editing) return;
    // Read from the ref so we capture the latest value even if the TextInput cleared its own state on submit.
    const trimmed = textValueRef.current.trim();
    const existing = annotations.find((a) => a.id === editing.annotationId);
    if (!existing || existing.type !== AnnotationType.Text) {
      setEditing(null);
      setTextValue('');
      return;
    }

    if (!trimmed) {
      // Empty text is never saved — remove the annotation entirely, whether new or existing.
      setAnnotations(annotations.filter((a) => a.id !== editing.annotationId));
    } else {
      if (!editing.isNew) pushUndoSnapshot();
      setAnnotations(annotations.map((a) => (a.id === editing.annotationId ? ({ ...a, text: trimmed } as TextAnnotation) : a)));
    }

    setEditing(null);
    setTextValue('');
    if (!isWeb) Keyboard.dismiss();
  }, [editing, annotations, setAnnotations, pushUndoSnapshot]);

  const cancel = useCallback(() => {
    if (editing?.isNew) {
      setAnnotations(annotations.filter((a) => a.id !== editing.annotationId));
    }
    setEditing(null);
    setTextValue('');
    if (!isWeb) Keyboard.dismiss();
  }, [editing, annotations, setAnnotations]);

  // Only update local textValue while typing; the annotation is written on commit.
  const updateText = useCallback((value: string) => {
    setTextValue(value);
  }, []);

  // Double-rAF focus for proper input mounting across zoom/layout updates. `select` /
  // `setSelectionRange` only exist on the web HTMLInputElement — calling them on a native
  // RN TextInput throws and silently aborts the focus, leaving the keyboard hidden.
  useEffect(() => {
    if (!editing) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        if (!isWeb) return;
        if (editing.selectAll) el.select();
        else el.setSelectionRange(el.value.length, el.value.length);
      });
    });
  }, [editing]);

  return {
    editing,
    textValue,
    inputRef,
    requestEditing,
    updateText,
    commit,
    cancel,
  };
};
